import type { Reading } from '@casa/driver-sdk';

// On and off as the readings of a light or a switch give them, for the `light` and `switch` tiles.

function isRecord(value: unknown) : value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOn(value: unknown) : boolean | null {
    if (isRecord(value)) {
        return isOn(value.switch);
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value > 0 : null;
    }
    if (value === 'on' || value === 'off') {
        return value === 'on';
    }

    return null;
}

/**
 * Whether a light or switch is on, from the last reading of its source: a device's attributes with
 * `switch: "on" | "off"` (as the Hubitat Maker API reports a light), `"on"` or `"off"`, a boolean,
 * or a level (on above 0). Null when it is not known.
 */
export function isSwitchedOn(readings: Reading[] | null | undefined) : boolean | null {
    const last = readings?.at(-1);

    return last ? isOn(last.value) : null;
}

/** The readings to show at once after switching: the last reading, switched, in its own form. */
export function switchedReadings(readings: Reading[] | null | undefined, on: boolean) : Reading[] {
    const last = readings?.at(-1);
    const value = last?.value;
    let switched: unknown;
    if (isRecord(value)) {
        switched = { ...value, switch: on ? 'on' : 'off' };
    } else if (typeof value === 'boolean') {
        switched = on;
    } else if (typeof value === 'number') {
        switched = on ? (value > 0 ? value : 100) : 0;
    } else {
        switched = on ? 'on' : 'off';
    }

    return [{ ...last, value: switched }];
}
